import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  InputAdornment,
  Grid,
  Switch,
  FormControlLabel,
  Skeleton,
  Chip,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  usePlan,
  useUpdatePlan,
  usePlanModules,
  useAssignModuleToPlan,
  useUpdatePlanModule,
  useRemoveModuleFromPlan,
} from '../../../hooks/subscriptions';
import { useModules } from '../../../hooks/subscriptions';
import { useSnackbar } from '../../../components/SnackbarProvider';
import { BillingInterval, Currency } from '../../../types/subscriptions';
import { getCurrencySymbol, getCategoryLabel } from '../../../utils/subscriptions';
import type { UpdatePlanDto, Module } from '../../../types/subscriptions';

export const PlanEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();

  const { data: plan, isLoading } = usePlan(id!);
  const updateMutation = useUpdatePlan(id!);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(Currency.DZD);

  // Plan modules management
  const { data: planModules, isLoading: modulesLoading } = usePlanModules(id!);
  const { data: allModules } = useModules();
  const assignModuleMutation = useAssignModuleToPlan(id!);
  const updatePlanModuleMutation = useUpdatePlanModule(id!);
  const removeModuleMutation = useRemoveModuleFromPlan(id!);
  const [selectedModuleToAdd, setSelectedModuleToAdd] = useState<Module | null>(null);
  const [newModuleAddOnPrice, setNewModuleAddOnPrice] = useState<string>('');
  const [newModuleIsIncluded, setNewModuleIsIncluded] = useState(true);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<UpdatePlanDto>();

  // Populate form when plan data loads
  useEffect(() => {
    if (plan) {
      reset({
        name: plan.name,
        slug: plan.slug,
        description: plan.description,
        basePrice: plan.basePrice,
        currency: plan.currency,
        billingInterval: plan.billingInterval,
        maxUsers: plan.maxUsers || 0,
        maxStores: plan.maxStores || 0,
        maxWarehouses: plan.maxWarehouses || 0,
        isActive: plan.isActive,
        displayOrder: plan.displayOrder,
      });
      setSelectedCurrency(plan.currency || Currency.DZD);
    }
  }, [plan, reset]);

  const onSubmit = (data: UpdatePlanDto) => {
    // Convert empty strings to null for numeric fields
    const payload = {
      ...data,
      maxUsers: !data.maxUsers || data.maxUsers === 0 ? null : Number(data.maxUsers),
      maxStores: !data.maxStores || data.maxStores === 0 ? null : Number(data.maxStores),
      maxWarehouses: !data.maxWarehouses || data.maxWarehouses === 0 ? null : Number(data.maxWarehouses),
    };

    updateMutation.mutate(payload, {
      onSuccess: () => {
        showMessage('Plan updated successfully', 'success');
        navigate('/subscriptions/plans');
      },
      onError: (error: any) => {
        showMessage(error.response?.data?.message || 'Failed to update plan', 'error');
      },
    });
  };

  // Get modules not yet assigned to this plan
  const assignedModuleIds = planModules?.map((pm) => pm.moduleId) || [];
  const availableModules = allModules?.filter((m) => !assignedModuleIds.includes(m.id)) || [];

  const handleAddModule = () => {
    if (!selectedModuleToAdd) return;

    assignModuleMutation.mutate(
      {
        moduleId: selectedModuleToAdd.id,
        isIncluded: newModuleIsIncluded,
        addOnPrice: newModuleAddOnPrice ? parseFloat(newModuleAddOnPrice) : undefined,
      },
      {
        onSuccess: () => {
          showMessage('Module added to plan', 'success');
          setSelectedModuleToAdd(null);
          setNewModuleAddOnPrice('');
          setNewModuleIsIncluded(true);
        },
        onError: (error: any) => {
          showMessage(error.response?.data?.message || 'Failed to add module', 'error');
        },
      }
    );
  };

  const handleRemoveModule = (moduleId: string) => {
    removeModuleMutation.mutate(moduleId, {
      onSuccess: () => {
        showMessage('Module removed from plan', 'success');
      },
      onError: (error: any) => {
        showMessage(error.response?.data?.message || 'Failed to remove module', 'error');
      },
    });
  };

  const handleUpdateModuleIncluded = (moduleId: string, isIncluded: boolean) => {
    updatePlanModuleMutation.mutate(
      { moduleId, dto: { isIncluded } },
      {
        onSuccess: () => {
          showMessage('Module updated', 'success');
        },
        onError: (error: any) => {
          showMessage(error.response?.data?.message || 'Failed to update module', 'error');
        },
      }
    );
  };

  const handleUpdateModulePrice = (moduleId: string, addOnPrice: number | undefined) => {
    updatePlanModuleMutation.mutate(
      { moduleId, dto: { addOnPrice } },
      {
        onSuccess: () => {
          showMessage('Module price updated', 'success');
        },
        onError: (error: any) => {
          showMessage(error.response?.data?.message || 'Failed to update price', 'error');
        },
      }
    );
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={48} sx={{ mb: 3 }} />
        <Card>
          <CardContent>
            <Skeleton variant="rectangular" height={400} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (!plan) {
    return (
      <Box>
        <Typography variant="h4" mb={3}>
          Plan Not Found
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/subscriptions/plans')}>
          Back to Plans
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" mb={3}>
        Edit Plan: {plan.name}
      </Typography>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('name', { required: 'Plan name is required' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                  label="Plan Name"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('slug', {
                    required: 'Slug is required',
                    pattern: {
                      value: /^[a-z0-9-]+$/,
                      message: 'Slug must be lowercase letters, numbers, and hyphens only'
                    }
                  })}
                  error={!!errors.slug}
                  helperText={errors.slug?.message || 'URL-friendly identifier'}
                  fullWidth
                  label="Slug"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  {...register('description', { required: 'Description is required' })}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                />
              </Grid>

              {/* Pricing */}
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Pricing
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  {...register('basePrice', {
                    required: 'Base price is required',
                    min: { value: 0, message: 'Price must be positive' }
                  })}
                  error={!!errors.basePrice}
                  helperText={errors.basePrice?.message}
                  fullWidth
                  label="Base Price"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">{getCurrencySymbol(selectedCurrency)}</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Controller
                    name="currency"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Currency"
                        onChange={(e) => {
                          field.onChange(e);
                          setSelectedCurrency(e.target.value as Currency);
                        }}
                      >
                        <MenuItem value={Currency.DZD}>DZD - Algerian Dinar</MenuItem>
                        <MenuItem value={Currency.EUR}>EUR - Euro</MenuItem>
                        <MenuItem value={Currency.USD}>USD - US Dollar</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth error={!!errors.billingInterval}>
                  <InputLabel>Billing Interval</InputLabel>
                  <Controller
                    name="billingInterval"
                    control={control}
                    rules={{ required: 'Billing interval is required' }}
                    render={({ field }) => (
                      <Select {...field} label="Billing Interval">
                        <MenuItem value={BillingInterval.MONTHLY}>Monthly</MenuItem>
                        <MenuItem value={BillingInterval.YEARLY}>Yearly</MenuItem>
                      </Select>
                    )}
                  />
                  {errors.billingInterval && (
                    <FormHelperText>{errors.billingInterval.message}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Resource Limits */}
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Resource Limits
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                  Leave at 0 for unlimited
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  {...register('maxUsers')}
                  fullWidth
                  label="Max Users"
                  type="number"
                  inputProps={{ min: 0 }}
                  helperText="0 = Unlimited"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  {...register('maxStores')}
                  fullWidth
                  label="Max Stores"
                  type="number"
                  inputProps={{ min: 0 }}
                  helperText="0 = Unlimited"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  {...register('maxWarehouses')}
                  fullWidth
                  label="Max Warehouses"
                  type="number"
                  inputProps={{ min: 0 }}
                  helperText="0 = Unlimited"
                />
              </Grid>

              {/* Settings */}
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Settings
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  {...register('displayOrder', {
                    min: { value: 0, message: 'Display order must be non-negative' }
                  })}
                  error={!!errors.displayOrder}
                  helperText={errors.displayOrder?.message || 'Lower numbers appear first'}
                  fullWidth
                  label="Display Order"
                  type="number"
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch {...field} checked={field.value} />}
                      label="Active (available for assignment)"
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Included Modules Section */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Included Modules
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Manage which modules are included in this plan. Modules marked as "Included" are automatically enabled when an organization subscribes. Add-on modules can be purchased separately.
              </Typography>

              {/* Add Module Form */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={4}>
                    <Autocomplete
                      options={availableModules}
                      getOptionLabel={(option) => `${option.name} (${option.key})`}
                      value={selectedModuleToAdd}
                      onChange={(_, value) => setSelectedModuleToAdd(value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Module"
                          placeholder="Search modules..."
                          size="small"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box>
                            <Typography variant="body2">{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.key} - {getCategoryLabel(option.category)}
                            </Typography>
                          </Box>
                        </li>
                      )}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={newModuleIsIncluded}
                          onChange={(e) => setNewModuleIsIncluded(e.target.checked)}
                          size="small"
                        />
                      }
                      label="Included"
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField
                      value={newModuleAddOnPrice}
                      onChange={(e) => setNewModuleAddOnPrice(e.target.value)}
                      label="Add-on Price (optional)"
                      type="number"
                      size="small"
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            {getCurrencySymbol(selectedCurrency)}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddModule}
                      disabled={!selectedModuleToAdd || assignModuleMutation.isPending}
                      fullWidth
                    >
                      Add Module
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              {/* Current Modules Table */}
              {modulesLoading ? (
                <Skeleton variant="rectangular" height={200} />
              ) : planModules && planModules.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Module</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="center">Included</TableCell>
                        <TableCell align="right">Add-on Price</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {planModules.map((pm) => (
                        <TableRow key={pm.id}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {pm.module?.name || 'Unknown'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {pm.module?.key}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {pm.module && (
                              <Chip
                                label={getCategoryLabel(pm.module.category)}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              checked={pm.isIncluded}
                              onChange={(e) =>
                                handleUpdateModuleIncluded(pm.moduleId, e.target.checked)
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              defaultValue={pm.addOnPrice || ''}
                              onBlur={(e) => {
                                const val = e.target.value
                                  ? parseFloat(e.target.value)
                                  : undefined;
                                if (val !== pm.addOnPrice) {
                                  handleUpdateModulePrice(pm.moduleId, val);
                                }
                              }}
                              type="number"
                              size="small"
                              sx={{ width: 120 }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    {getCurrencySymbol(selectedCurrency)}
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Remove from plan">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveModule(pm.moduleId)}
                                disabled={removeModuleMutation.isPending}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No modules assigned to this plan yet. Add modules above.
                </Typography>
              )}
            </Box>

            <Box display="flex" gap={2} mt={4}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/subscriptions/plans')}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
